/**
 * StateHoverCard — small floating panel shown when the user hovers a
 * sub-national region (US state, Spanish CCAA/province, AU state, etc.).
 * Sits to the right of the country card so you get both levels of context.
 *
 * If we have a rule recorded for the region, we show its tier pill, title,
 * and full description. Otherwise we show "(Parent-country tier applies)".
 */
import { theme, FONT_STACK } from '@/analyses/_shared/theme';
import { TIER_CONFIG } from '@/data/tiers';
import type { HoveredSubRegion } from '@/components/SubnationalLayer';

export type StateHoverCardProps = {
  hovered: HoveredSubRegion | null;
};

export const StateHoverCard = ({ hovered }: StateHoverCardProps) => {
  if (!hovered) return null;
  const { country, regionName, regionCode, rule, tier } = hovered;
  const tierCfg = TIER_CONFIG[tier];

  return (
    <div
      className="no-print"
      style={{
        position: 'fixed',
        // Top-right corner — below the zoom controls (which sit top:20, height~140),
        // above the hint strip. Doesn't collide with the bottom-left country card.
        top: 180,
        right: 24,
        background: theme.CARD,
        border: `1px solid ${theme.BORDER}`,
        borderLeft: `3px solid ${tierCfg.color}`,
        borderRadius: 10,
        padding: '12px 14px',
        minWidth: 260,
        maxWidth: 380,
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
        zIndex: 39,
        fontFamily: FONT_STACK,
      }}
    >
      {/* Header row: tier dot + region name + code */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: tierCfg.color,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            color: theme.WHITE,
            fontSize: 13.5,
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {regionName}
        </span>
        <span style={{ color: theme.MUTED, fontSize: 10.5 }}>
          {regionCode}
        </span>
      </div>

      {/* Parent country breadcrumb */}
      <div
        style={{
          color: theme.MUTED,
          fontSize: 11,
          marginBottom: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span>{country.name}</span>
        <span style={{ color: theme.BORDER }}>·</span>
        <span
          style={{
            color: tierCfg.color,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: 10,
          }}
        >
          {tierCfg.label}
        </span>
      </div>

      {/* Rule body — title + description — or fallback */}
      {rule ? (
        <>
          <div
            style={{
              color: theme.WHITE,
              fontSize: 12.5,
              fontWeight: 600,
              marginBottom: 4,
              lineHeight: 1.35,
            }}
          >
            {rule.title}
          </div>
          <div
            style={{
              color: theme.TEXT,
              fontSize: 11.5,
              lineHeight: 1.55,
            }}
          >
            {rule.description}
          </div>
          {rule.legalRef && (
            <div
              style={{
                color: theme.MUTED,
                fontSize: 10,
                marginTop: 6,
                fontStyle: 'italic',
              }}
            >
              {rule.legalRef}
            </div>
          )}
          <div
            style={{
              display: 'inline-block',
              marginTop: 8,
              padding: '2px 8px',
              borderRadius: 4,
              background:
                rule.ruleType === 'openMarket'
                  ? `${theme.GREEN}22`
                  : `${theme.RED}22`,
              color:
                rule.ruleType === 'openMarket' ? theme.GREEN : theme.RED,
              fontSize: 9.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            {rule.ruleType === 'openMarket' ? 'Open market' : 'Enforcement'}
          </div>
        </>
      ) : (
        <div
          style={{
            color: theme.MUTED,
            fontSize: 11.5,
            fontStyle: 'italic',
            lineHeight: 1.5,
          }}
        >
          No sub-national rule on file for this region. Parent country tier
          applies by default. Click to open the {country.name} analysis.
        </div>
      )}

      <div
        style={{
          color: theme.MUTED,
          fontSize: 10,
          marginTop: 8,
          fontStyle: 'italic',
        }}
      >
        Click to open {country.name} analysis
      </div>
    </div>
  );
};
